import Divider from '@mui/material/Divider';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import {
  FC, useCallback, useMemo, useEffect, useState,
} from 'react';
import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingButton from '@mui/lab/LoadingButton';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import {
  BaseDrawer,
  BaseDrawerTitle,
  BaseLabel,
  BaseCombobox,
} from '@mgm/common/components/base';
import {
  CustomTextField,
} from '@mgm/common/components/form';
import { BASE_COLORS } from '@mgm/common/utils';
import { PermissionCard } from '@mgm/common/features/team';
import { TLocation } from '../../../types';
import {
  EmailField,
} from '../../../components/form';

import { useOptions } from '../../../hooks/useOptions';
import { useAppDispatch, useAppSelector } from '../../../hooks/useAppStore';
import { getPlatformData } from '../../../store/common/selectors';
import { getInviteMembers } from '../../../store/onboarding/selector';
import { onboardingActions } from '../../../store/onboarding/slice';
import { commonActions } from '../../../store/common/slice';

type TInviteMember = {
  permissionLevel: number;
  locations: Array<TLocation>;
  firstName: string;
  lastName: string;
  email: string;
};

type TInviteAddMemberNav = {
  isOpenNav: boolean;
  handleCloseNav: () => void;
  mode: string;
  setFormInputs: (val: (prevState: TInviteMember) => any) => void;
  formInputs: TInviteMember;
  isOnboarding?: boolean
};

const InviteAddMemberNav: FC<TInviteAddMemberNav> = ({
  isOpenNav,
  handleCloseNav,
  mode,
  setFormInputs,
  formInputs,
  isOnboarding = true,
}) => {
  const theme = useTheme() as TCustomTheme;
  const { isMobile } = useCustomTheme(theme);
  const platformData = useAppSelector(getPlatformData);
  const inviteMembers = useAppSelector(getInviteMembers);
  const dispatch = useAppDispatch();
  const [isSave, setIsSave] = useState(false);

  const {
    roleOptions,
  } = useOptions();

  const { t } = useTranslation(['inputs', 'common', 'welcome', 'team', 'settings']);

  const methods = useForm({
    mode: 'all',
    values: {
      firstName: formInputs.firstName || '',
      lastName: formInputs.lastName || '',
      email: formInputs?.email || '',
      permissionLevel: formInputs.permissionLevel || '',
      locations: formInputs?.locations || [],
    },
  });

  // Watch the email field for changes
  const email = useWatch({
    control: methods.control,
    name: 'email',
  });

  const [initialEmail, setInitialEmail] = useState(formInputs.email);

  useEffect(() => {
    if (mode === 'edit') {
      setInitialEmail(formInputs.email);
    }
  }, [formInputs.email, mode]);

  const isDuplicateEmail = useMemo(() => {
    if ((mode === 'edit' && email === initialEmail) || isSave) {
      return false;
    }
    return inviteMembers.some((member) => member.email?.toLowerCase() === email?.toLowerCase());
  }, [inviteMembers, email, mode, initialEmail, isSave]);

  const addMember = () => {
    if (!isDuplicateEmail) {
      setIsSave(true);

      dispatch(onboardingActions.setInviteMembers([...inviteMembers, formInputs]));
      handleCloseNav();

      setTimeout(() => {
        setIsSave(false);
      }, 400);
    }
  };

  const editMember = () => {
    if (!isDuplicateEmail) {
      dispatch(onboardingActions.setInviteMembers(
        inviteMembers.map((member) => (member.email === formInputs.email ? formInputs : member)),
      ));
      handleCloseNav();
    }
  };

  const handleFormChange = useCallback((key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  }, []);

  useEffect(() => {
    methods.setValue('email', formInputs.email);
  }, [formInputs.email, methods]);

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';

    const actionTitle = isCreateMode ? t('addTeamMember', { ns: 'team' }) : t('editTeamMember', { ns: 'team' });

    return {
      title: actionTitle,
      mainBtnText: isCreateMode ? t('addMember', { ns: 'team' }) : t('saveChanges', { ns: 'settings' }),
      isCreateMode,
    };
  }, [mode]);

  const saveAndAdd = () => {
    if (!isDuplicateEmail) {
      setIsSave(true);
      dispatch(onboardingActions.setInviteMembers([...inviteMembers, formInputs]));
      setFormInputs({
        locations: [{
          id: 1,
          name: 'Back Office',
        }],
        permissionLevel: 0,
        firstName: '',
        lastName: '',
        email: '',
      } as any);

      setIsSave(false);
    }
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      isDisplayToolbar={!isOnboarding}
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      open={isOpenNav}
      onCloseDrawer={onCloseDrawer}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          mt={3}
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={dataByMode.isCreateMode ? methods.handleSubmit(addMember) : methods.handleSubmit(editMember)}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid
              id="first-name"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <CustomTextField
                onChange={(firstName) => {
                  handleFormChange('firstName', firstName);
                }}
                isRequiredField
                placeholder={t('enterFirstName')}
                labelText={t('firstName')}
                name="firstName"
              />
            </Grid>
            <Grid
              id="last-name"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <CustomTextField
                onChange={(lastName) => {
                  handleFormChange('lastName', lastName);
                }}
                isRequiredField
                placeholder={t('enterLastName')}
                labelText={t('lastName')}
                name="lastName"
              />
            </Grid>
            <Grid
              id="invite-email"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <EmailField
                disabled={mode === 'edit'}
                onChange={(email) => handleFormChange('email', email)}
                isRequired
                error={isDuplicateEmail}
                helperText={isDuplicateEmail ? t('emailAlreadyExists', { ns: 'inputs' }) : ''}
              />
            </Grid>
            <Grid
              id="invite-locations"
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <div className="justify-space-between">
                <div className="align-center">
                  <BaseLabel letterSpacing="-0.5px">{t('locations')}</BaseLabel>
                  <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
                </div>
              </div>
              <BaseCombobox<TLocation>
                multiple
                variant="outlined"
                value={formInputs.locations}
                options={platformData?.locations || []}
                onChange={(locations) => handleFormChange('locations', locations)}
              />
            </Grid>
            <Grid
              id="invite-permissions"
              size={{
                xs: 12,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('permissions')}</BaseLabel>
              <Box pt={2}>
                {roleOptions.map((role) => (
                  <PermissionCard
                    key={role.id}
                    value={role.value}
                    selectedValue={formInputs.permissionLevel}
                    onChange={(value) => handleFormChange('permissionLevel', value)}
                    title={role.name}
                    description={role.tooltipText}
                  />
                ))}
              </Box>
            </Grid>
            <Grid size={{
              xs: 12,
              sm: 6,
              md: 6,
              lg: 6,
            }}
            >
              <LoadingButton
                id="invite-add-button"
                disabled={!methods.formState.isValid || !formInputs.locations?.length || isDuplicateEmail}
                type="submit"
                size="large"
                variant="contained"
                fullWidth
              >
                {dataByMode.mainBtnText}
              </LoadingButton>
            </Grid>
            {mode !== 'edit' && (
            <Grid
              {...isMobile && {
                pt: 0,
              }}
              size={{
                xs: 12,
                sm: 6,
                md: 6,
                lg: 6,
              }}
            >
              <LoadingButton
                id="invite-add-more"
                disabled={!methods.formState.isValid || !formInputs.locations?.length || isDuplicateEmail}
                onClick={saveAndAdd}
                fullWidth
                size="large"
                variant="outlined"
              >
                {t('saveAndAdd', { ns: 'inputs' })}
              </LoadingButton>
            </Grid>
            )}
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  InviteAddMemberNav,
};
